/**
 * 사진 촬영/갤러리 선택 공통 컴포넌트
 *
 * 단일 레이아웃: 세로 (촬영 위 + 갤러리 아래, 4:3 비율)
 *
 * 핵심 로직 통합:
 * - 네이티브: InAppCamera 연동 (onCameraOpen 콜백)
 * - 웹: <input type="file" capture="environment"> 폴백
 * - 갤러리: <input type="file" accept="image/*"> (capture 없음)
 */

import { Camera, ImagePlus, Loader2 } from "lucide-react";
import { useFileCapture } from "@/hooks/useFileCapture";
import { HiddenFileInputs } from "@/components/inspection/shared/HiddenFileInputs";

// ============================================================================
// Types
// ============================================================================

export interface PhotoCaptureSlotProps {
  /** 슬롯 라벨 (예: "점검사진", "결과 사진") */
  label?: string;
  /** 파일 선택 콜백 (카메라 촬영 또는 갤러리 선택) */
  onFileSelect: (file: File) => void;
  /** 네이티브 인앱 카메라 열기 콜백 (네이티브 환경에서만 전달) */
  onCameraOpen?: () => void;
  /** 업로드 중 여부 */
  uploading?: boolean;
  /** 비활성 상태 */
  disabled?: boolean;
  /** 갤러리에서 다중 파일 선택 시 콜백 (사진대지 전용) */
  onMultiFileSelect?: (files: File[]) => void;
  /** 갤러리 input에서 다중 선택 허용 여부 (기본: false) */
  galleryMultiple?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function PhotoCaptureSlot({
  label,
  onFileSelect,
  onCameraOpen,
  uploading = false,
  disabled = false,
  onMultiFileSelect,
  galleryMultiple = false,
}: PhotoCaptureSlotProps) {
  const {
    cameraInputRef,
    galleryInputRef,
    handleCameraClick,
    handleGalleryClick,
    handleFileChange,
    galleryMultiple: resolvedGalleryMultiple,
  } = useFileCapture({ onFileSelect, onCameraOpen, onMultiFileSelect, galleryMultiple });

  const isDisabled = disabled || uploading;

  return (
    <>
      <div className="flex flex-col gap-2">
        {/* 메인 슬롯 - 클릭 시 카메라 촬영 */}
        <button
          type="button"
          onClick={handleCameraClick}
          disabled={isDisabled}
          aria-label={label ? `${label} 촬영` : "사진 촬영"}
          className="relative w-full aspect-[4/3] rounded-xl bg-gradient-to-b from-slate-100 to-slate-50 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-2 active:border-blue-300 active:from-blue-50 active:to-slate-50 transition-all duration-150 disabled:opacity-50 overflow-hidden"
        >
          {uploading ? (
            <>
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              </div>
              <span className="text-xs font-medium text-blue-500">업로드 중...</span>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full bg-slate-200/80 flex items-center justify-center">
                <Camera className="w-5 h-5 text-slate-500" />
              </div>
              {label && <span className="text-xs font-medium text-slate-500">{label}</span>}
              <span className="text-[10px] text-slate-400">탭하여 촬영</span>
            </>
          )}
        </button>

        {/* 갤러리 버튼 */}
        <button
          type="button"
          onClick={handleGalleryClick}
          disabled={isDisabled}
          className="group relative w-full py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center gap-1.5 active:scale-[0.98] active:from-blue-600 active:to-blue-700 transition-all duration-150 disabled:opacity-50 shadow-sm shadow-blue-500/25"
          aria-label={label ? `${label} 갤러리에서 선택` : "갤러리에서 선택"}
        >
          <ImagePlus className="w-4 h-4 text-white" />
          <span className="text-xs font-semibold text-white">갤러리에서 선택</span>
        </button>
      </div>

      <HiddenFileInputs
        cameraInputRef={cameraInputRef}
        galleryInputRef={galleryInputRef}
        onFileChange={handleFileChange}
        galleryMultiple={resolvedGalleryMultiple}
      />
    </>
  );
}
